import React from "react";
import dayjs from "dayjs";
import styles from "./WelcomeBack.module.scss";
import { KnownPatient } from "api/getKnownPatientRequest";
import { doctorFullName } from "utils/doctorFullName";

interface WelcomeBackProps {
  patient: KnownPatient;
  mobile?: boolean;
}

interface ElapsedTime {
  unit: "day" | "month" | "year";
  value: number;
}

const elapsedTimeSince = (date: string): ElapsedTime | null => {
  const appointmentDate = dayjs(date);
  if (!appointmentDate.isValid()) return null;

  const years = dayjs().diff(appointmentDate, "year");
  if (years >= 1) return { unit: "year", value: years };

  const months = dayjs().diff(appointmentDate, "month");
  if (months >= 1) return { unit: "month", value: months };

  return {
    unit: "day",
    value: Math.max(0, dayjs().diff(appointmentDate, "day")),
  };
};

const mixedCaseName = (name: string): string => {
  const trimmedName = name.trim();
  if (/[A-Z]/.test(trimmedName) && /[a-z]/.test(trimmedName)) {
    return trimmedName;
  }

  return trimmedName
    .toLowerCase()
    .replace(/(^|[\s'-])([a-z])/g, (_match, prefix, letter) =>
      `${prefix}${letter.toUpperCase()}`
    );
};

const doctorName = (patient: KnownPatient): string | null => {
  const firstName = patient.lastAppointment?.doctorFirstName;
  const lastName = patient.lastAppointment?.doctorLastName;
  if (!firstName || !lastName) return null;
  const providerSuffix =
    patient.lastAppointment?.doctorProviderSuffix?.trim().toUpperCase() ||
    undefined;

  return doctorFullName({
    id: "known-patient-last-doctor",
    firstName: mixedCaseName(firstName),
    lastName: mixedCaseName(lastName),
    providerSuffix,
  });
};

export const WelcomeBack: React.FC<WelcomeBackProps> = ({
  patient,
  mobile = false,
}) => {
  const displayName = mobile
    ? patient.firstName || "there"
    : [patient.firstName, patient.lastName].filter(Boolean).join(" ") ||
      "there";
  const lastAppointment = patient.lastAppointment;
  const elapsedTime = lastAppointment?.date
    ? elapsedTimeSince(lastAppointment.date)
    : null;
  const doctor = doctorName(patient);
  const elapsedTimeLabel = elapsedTime
    ? `${elapsedTime.unit}${elapsedTime.value === 1 ? "" : "s"}`
    : null;

  return (
    <section className={styles.container} aria-label="Welcome back">
      <h2 className={styles.heading}>Welcome back, {displayName}</h2>
      <p className={styles.detail}>
        {elapsedTime && doctor ? (
          <>
            {mobile ? "It’s" : "It's"} been {elapsedTime.value}{" "}
            {elapsedTimeLabel} since your last appointment with{" "}
            <strong>{doctor}</strong>, at this location.
            {mobile && " We’re glad to see you!"}
          </>
        ) : (
          "We're glad to see you again!"
        )}
      </p>
    </section>
  );
};
